import type { ConnectedChatSessionSnapshot } from "../generated/connectedChat";

import { reploEnvSchema } from "replo-utils/lib/env";
import { z } from "zod";

import { agentPermissionScopeSchema } from "../agentPermissions";

// NOTE (Yuxin, 2026-07-20): not `RequestInfo` — that type is lib.dom/workers-only and node-lib projects import this file.
export type WebhookResponderFetcher = {
  fetch(input: string | URL | Request, init?: RequestInit): Promise<Response>;
};

// NOTE (Kevin, 2026-07-22): Optional fields are nullish to match the webhook
// responder's nullable persisted state without a mapping layer.
export const linqDeliveryReferenceSchema = z
  .object({
    coordinatorId: z.string().min(1),
    deliveryId: z.string().min(1),
  })
  .describe("LinqDeliveryReference");

const connectedChatDeliveryStateBaseSchema = z.object({
  lastSeq: z.number(),
  isFinal: z.boolean().optional(),
  lastAssistantText: z.string().nullish(),
  // An empty successful final can recover streamed text only for this message.
  responseTextRecovery: z
    .object({ messageId: z.string().min(1), text: z.string() })
    .nullish(),
  projectName: z.string(),
  projectUrl: z.string(),
  queue: z
    .object({
      // The processing-queue DO's name: a deterministic uuid — the thread
      // session id for Slack, the hashed conversation id for Messages — so no
      // raw transport identity (phone number) ever names a DO.
      id: z.uuid(),
      itemId: z.string(),
    })
    .nullish(),
});

export const slackWebhookDeliveryStateSchema =
  connectedChatDeliveryStateBaseSchema
    .extend({
      provider: z.literal("slack"),
      messageTs: z.string().nullish(),
      // The card's one title: seeded from the user's request at prompt time,
      // overwritten whenever a newer set_status arrives.
      title: z.string().nullish(),
      // The turn's latest main-session snapshot, persisted so a subagent event
      // can re-render the card with the main turn's structure.
      mainSnapshot: z
        .custom<ConnectedChatSessionSnapshot>()
        .nullish()
        .describe("override?:ConnectedChatSessionSnapshot | null"),
      // Max tool count seen per subagent session this turn; summed with the main
      // count for a "Ran N actions" counter that never decreases.
      subagentActionCounts: z.record(z.string(), z.number()).nullish(),
      channelId: z.string(),
      threadId: z.string().nullish(),
      nangoConnectionId: z.string(),
      externalActorId: z.string(),
      externalWorkspaceId: z.string(),
      reactionTarget: z
        .object({
          channelId: z.string(),
          timestamp: z.string(),
        })
        .nullish(),
    })
    .describe("SlackWebhookDeliveryState");

// One outstanding ask on a Messages session. Linq has no per-message metadata
// (unlike Slack buttons), so a pending question/permission is remembered on the
// delivery state and the next inbound text is read as the answer/decision.
export const linqPendingQuestionStepSchema = z
  .object({
    prompt: z.string(),
    allowMultiple: z.boolean(),
    custom: z.boolean(),
    options: z.array(
      z.object({
        id: z.string(),
        label: z.string(),
        // Some questions illustrate each choice (e.g. design directions). Kept
        // on the step so a later step re-renders with its own images.
        imageUrl: z.string().nullish(),
      }),
    ),
    // The first option, which the timer submits when nobody replies.
    releaseLabel: z.string().optional(),
  })
  .describe("LinqPendingQuestionStep");

export const linqPendingAskSchema = z
  .discriminatedUnion("kind", [
    z.object({
      kind: z.literal("permission"),
      permissionId: z.string(),
      agentSessionId: z.string().optional(),
      expectedScopes: z.array(agentPermissionScopeSchema).optional(),
      requiresUi: z.boolean().optional(),
      // The tool/action being requested, and what it wants to touch (a command,
      // a path), for the prompt text.
      action: z.string(),
      description: z.string().optional(),
      patterns: z.array(z.string()),
      askedAtMs: z.number(),
    }),
    z.object({
      kind: z.literal("question"),
      questionId: z.string(),
      toolCallId: z.string().optional(),
      // Each question step keeps its options so a numbered reply maps back to an
      // option id; answers accumulate across steps, submitted on the last.
      steps: z.array(linqPendingQuestionStepSchema),
      collectedAnswers: z.array(z.array(z.string())),
      askedAtMs: z.number(),
      releaseAtMs: z.number().nullish(),
    }),
  ])
  .describe("LinqPendingAsk");

// Messages (Linq) delivery is final-text only: no card, no editing. It carries
// the chat thread to reply into and the one outstanding ask (if any).
export const linqWebhookDeliveryStateSchema =
  connectedChatDeliveryStateBaseSchema
    .extend({
      provider: z.literal("linq"),
      afterDelivery: linqDeliveryReferenceSchema.optional(),
      // The chat thread to reply into; the responder sends worker-side with the
      // global Linq key, so no per-connection identifier is needed here.
      chatId: z.string(),
      pendingAsk: linqPendingAskSchema.nullish(),
      // Set once the "open in Replo" link has been texted, so it goes out on the
      // first reply of a conversation rather than on every message.
      sharedProjectLink: z.boolean().nullish(),
      // Set once the "save Replo as a contact" prompt has been offered.
      sharedContactCard: z.boolean().nullish(),
    })
    .describe("LinqWebhookDeliveryState");

export const webhookDeliveryStateSchema = z
  .discriminatedUnion("provider", [
    slackWebhookDeliveryStateSchema,
    linqWebhookDeliveryStateSchema,
  ])
  .describe("WebhookDeliveryState");

export const connectedChatDeliveryStatePatchSchema =
  slackWebhookDeliveryStateSchema
    .pick({
      mainSnapshot: true,
      messageTs: true,
      nangoConnectionId: true,
      subagentActionCounts: true,
      title: true,
    })
    // Linq sets/clears its outstanding ask and marks the shared link through
    // the same patch channel.
    .extend({
      pendingAsk: linqPendingAskSchema.nullish(),
      sharedProjectLink: z.boolean().nullish(),
      sharedContactCard: z.boolean().nullish(),
    })
    .partial()
    .describe("ConnectedChatDeliveryStatePatch");

// The delivery-state slice the Slack turn renderer reads.
export const slackTurnRenderStateSchema = slackWebhookDeliveryStateSchema
  .pick({
    channelId: true,
    externalActorId: true,
    externalWorkspaceId: true,
    mainSnapshot: true,
    projectName: true,
    projectUrl: true,
    queue: true,
    subagentActionCounts: true,
    title: true,
  })
  .describe("SlackTurnRenderState");

export const connectedChatDeliveryResultSchema = z
  .discriminatedUnion("status", [
    z.object({
      status: z.literal("delivered"),
      statePatch: connectedChatDeliveryStatePatchSchema,
    }),
    z.object({
      status: z.literal("aborted"),
      reason: z.enum([
        "connection_missing",
        "slack_auth_revoked",
        "linq_auth_revoked",
        "linq_send_rejected",
      ]),
    }),
  ])
  .describe("ConnectedChatDeliveryResult");

export type WebhookResponderDurableObjectStub = {
  fetch(input: string | URL | Request, init?: RequestInit): Promise<Response>;
};

export type WebhookResponderDurableObjectNamespace = {
  getByName(name: string): WebhookResponderDurableObjectStub;
};

const webhookResponderFetcherSchema = z
  .object({
    fetch: z.custom<WebhookResponderFetcher["fetch"]>(
      (value) => typeof value === "function",
    ),
  })
  .passthrough()
  .describe("WebhookResponderFetcher");

const webhookResponderDurableObjectNamespaceSchema = z
  .object({
    getByName: z.custom<WebhookResponderDurableObjectNamespace["getByName"]>(
      (value) => typeof value === "function",
    ),
  })
  .passthrough()
  .describe("WebhookResponderDurableObjectNamespace");

export const webhookResponderEnvSchema = z
  .object({
    AGENT_COORDINATOR_BASE_URL: z.string(),
    AGENT_COORDINATOR_SERVICE: webhookResponderFetcherSchema,
    AGENT_UI_BASE_URL: z.string(),
    BEDROCK_API: webhookResponderFetcherSchema,
    BEDROCK_INTERNAL_API_BASE_URL: z.string(),
    AGENT_COORDINATOR_SERVICE_TOKEN: z.string(),
    CLOUDFLARE_AGENT_WEBHOOK_RESPONDER_SQS_AWS_ACCESS_KEY_ID: z.string().min(1),
    CLOUDFLARE_AGENT_WEBHOOK_RESPONDER_SQS_AWS_SECRET_ACCESS_KEY: z
      .string()
      .min(1),
    CLOUDFLARE_AGENT_WEBHOOK_RESPONDER_SQS_QUEUE_URL: z.string().min(1),
    CLOUDFLARE_AGENT_WEBHOOK_RESPONDER_SQS_REGION: z.string().min(1),
    EVENTBRIDGE_CONNECTION_API_KEY: z.string().min(1),
    NANGO_SECRET_KEY: z.string(),
    NANGO_WEBHOOK_SIGNING_KEY: z.string(),
    // Messages (Linq) is a single Replo-owned partner account: one global API
    // key sends every outbound reply, and one global Standard Webhooks secret
    // verifies every inbound message. Both live here (worker-side), mirroring
    // the Slack signing-secret pattern — there are no per-connection credentials.
    LINQ_API_KEY: z.string(),
    LINQ_WEBHOOK_SIGNING_SECRET: z.string(),
    REPLO_ENV: reploEnvSchema,
    REPLO_DEV_ALIAS: z.string().optional(),
    REPLO_INITIAL_SNAPSHOT_REF: z.string().optional(),
    SENTRY_DSN: z.string(),
    SLACK_SIGNING_SECRET: z.string(),
    // Presented by the coordinator to authenticate pushes TO this responder.
    AGENT_WEBHOOK_RESPONDER_SERVICE_TOKEN: z.string(),
    // Presented by this responder to authenticate itself TO bedrock-api.
    BEDROCK_API_SERVICE_TOKEN: z.string(),
    WEBHOOK_SESSION_COORDINATOR: webhookResponderDurableObjectNamespaceSchema,
  })
  .describe("WebhookResponderEnv");
